/**
 * E5.4's density by relief — a proposal for the owner (his words, Oct 12: "not sure, I don't want everything to now be
 * dense forests. But I would like to have some dense forests where it makes sense."). Today a wooded region of the planet
 * forest (core/forest.ts) is full wherever it grows: every candidate stand that fits becomes one — a flat band. Here a
 * stand's chance follows the ground and its biome:
 *
 * - **Valleys and basins dense, ridges and plateaus open:** the ground's height against its surroundings at two
 *   breadths (RELIEF_WOODS.localPeople, broadPeople) — below them, woods; above them, open.
 * - **Drainage lines wooded:** the water's way down the coarse mesh (each vertex sheds to its lowest neighbour; the flow
 *   is the vertices upstream) — belts along the lines that gather it.
 * - **Lee slopes wooded, windward open:** the slope's fall against the prevailing wind — the bands about the match's own
 *   wind axis (core/windField.ts windAxis, the match's seed), so the woods and the weather agree.
 * - **Steep ground thinner** past STAND_ANCHOR_MAX_SLOPE's half.
 * - **Only the wooded biomes go dense:** each biome's ceiling (RELIEF_WOODS.ceiling) — the open woodlands (heath,
 *   savanna, scrub, ash, the burn) stay sparse wherever they grow.
 *
 * Per planet, from its ground at setup (the forest's basis): the coarse mesh's vertices welded, then the fields once,
 * read between the three vertices of the triangle over a point. Pure and deterministic (dmath; a fixed order); cached by
 * the base positions, the heightmap and the wind seed. Off unless asked (`'relief'`): the forest stays flat — nothing moves.
 */
import type { BiomeId, PlanetState, Vec3 } from '../types';
import { dasin, dlog, dsin } from './dmath';
import { getSurfaceRadius, lastSurfaceTriangle } from './surface';
import { bandShare, windAxis } from './windField';

/** 'flat': the old band; 'relief': E5.4's chance by the ground; 'climate': 393 (3) step 2 — the stands by the woods density field (core/woodsDensity.ts), the relief one factor of it. */
export type ForestDensityMode = 'flat' | 'relief' | 'climate';

export const RELIEF_WOODS = {
  /** The breadths the ground is read against (person heights): a valley's, and a basin's or a plateau's. */
  localPeople: 150,
  broadPeople: 600,
  /** Heights (person heights) at which a valley or a basin counts in full (and a ridge or a plateau, negated). */
  localDepthPeople: 3,
  broadDepthPeople: 10,
  /** The flow (vertices upstream) where a drainage line starts to count, and the doublings over which it counts in full. */
  flowFrom: 12,
  flowDoublings: 3,
  /** Each term's weight in the ground's score for woods. */
  valley: 0.35, basin: 0.25, drainage: 0.35, lee: 0.15, steep: 0.6,
  /**
   * Calibrated per planet by share of its ground, so every planet has "some dense forests where it makes sense", in the
   * same measure whatever its relief: the lowest-scoring `openShare` is open (a stand's chance `openChance`), the top
   * `denseShare` the dense core (0.9 to 1), a ramp between.
   */
  openShare: 0.35, denseShare: 0.12, openChance: 0.1,
  /**
   * The dense core: where a stand's chance (the ground's lie, the ceiling aside) is at least `denseFrom`, the stands pack
   * closer (the gap between them × `denseGap`) and hold more trees, tighter (× `denseTrees`, spacing × `denseSpacing`) —
   * real forest in the valleys and along the drainage, not today's band spread thinner.
   */
  denseFrom: 0.9, denseGap: 0.25, denseTrees: 1.4, denseSpacing: 0.85,
  /** Each biome's ceiling: the wooded ones go dense where the ground says; the open woodlands stay sparse. */
  ceiling: { broadleaf: 1, firStand: 1, birchGlade: 1, lanternGrove: 1, hollowWood: 1, heath: 0.45, savanna: 0.35, scrub: 0.45, ashBarrens: 0.4, burn: 0.6 } as Record<BiomeId, number>,
};

/** The planet's relief, read for the woods: per welded vertex of the coarse mesh. */
export interface ReliefWoods {
  /** each mesh vertex entry's welded vertex */
  vertexOf: Int32Array;
  /** per welded vertex: local and broad height against the surroundings (person heights), the flow (vertices upstream),
   *  the slope (rise over run), the lee (the downslope's share along the prevailing wind, −1 … 1) */
  local: Float64Array; broad: Float64Array; flow: Float64Array; slope: Float64Array; lee: Float64Array;
  /** per welded vertex: the ground's score for woods (rawScore); and the planet's open and dense quantiles of it, and its top */
  score: Float64Array; open: number; dense: number; top: number;
}

/** The ground's score for woods from its relief (no ceiling, no calibration): valley, basin, drainage and lee up, steepness down. */
function rawScore(local: number, broad: number, flow: number, slope: number, lee: number): number {
  const W = RELIEF_WOODS, clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));
  const valley = clamp(-local / W.localDepthPeople, -1, 1), basin = clamp(-broad / W.broadDepthPeople, -1, 1);
  const drainage = clamp((dlog(Math.max(1, flow) / W.flowFrom) / dlog(2)) / W.flowDoublings, 0, 1);
  return W.valley * valley + W.basin * basin + W.drainage * drainage + W.lee * lee * Math.min(1, slope * 4) - W.steep * Math.max(0, slope - 0.25);
}

const cache = new WeakMap<Float32Array, WeakMap<Float32Array, Map<string, ReliefWoods>>>();

/**
 * 69.84: the last few reliefs by content as well — the same planet built afresh (a new match on the same seed: every test's
 * setup, the suite's largest single cost) has new arrays, so the cache above misses and the walk ran again, ~2 s a large
 * planet. Each kept with copies of its ground, matched only when equal bit for bit, so it serves exactly what `build`
 * would make.
 */
const byContent: { basePositions: Float32Array; heightmap: Float32Array; windSeed: string; person: number; relief: ReliefWoods }[] = [];
const BY_CONTENT_KEPT = 4;
const sameBits = (a: Float32Array, b: Float32Array): boolean => {
  if (a.length !== b.length) return false;
  const x = new Uint32Array(a.buffer, a.byteOffset, a.length), y = new Uint32Array(b.buffer, b.byteOffset, b.length);
  for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return false;
  return true;
};

/**
 * The planet's relief for the woods (cached by its base positions, heightmap and `windSeed`, the match's: GameConfig.seed).
 * About 0.2 s a planet: core/forestHeld.ts initForest builds it at setup, so no tick pays for it.
 */
export function reliefWoods(planet: PlanetState, windSeed: string, person: number): ReliefWoods {
  let byHeights = cache.get(planet.basePositions);
  if (!byHeights) { byHeights = new WeakMap(); cache.set(planet.basePositions, byHeights); }
  let bySeed = byHeights.get(planet.heightmap);
  if (!bySeed) { bySeed = new Map(); byHeights.set(planet.heightmap, bySeed); }
  let r = bySeed.get(windSeed);
  if (!r) {
    const bp = planet.basePositions, hm = planet.heightmap;
    r = byContent.find(e => e.windSeed === windSeed && e.person === person && sameBits(e.basePositions, bp) && sameBits(e.heightmap, hm))?.relief;
    if (!r) {
      r = build(planet, windSeed, person);
      byContent.unshift({ basePositions: bp.slice(), heightmap: hm.slice(), windSeed, person, relief: r });
      if (byContent.length > BY_CONTENT_KEPT) byContent.pop();
    }
    bySeed.set(windSeed, r);
  }
  return r;
}

function build(planet: PlanetState, windSeed: string, person: number): ReliefWoods {
  const bp = planet.basePositions, hm = planet.heightmap, entries = Math.floor(bp.length / 3);
  // weld the mesh's vertex entries (each triangle its own three) by position, numbered in entry order: the entries sorted
  // by position, each run of equal ones a vertex, the vertices then numbered by their first entry
  const byPos = new Int32Array(entries);
  for (let i = 0; i < entries; i++) byPos[i] = i;
  byPos.sort((i, j) => (bp[i * 3] - bp[j * 3]) || (bp[i * 3 + 1] - bp[j * 3 + 1]) || (bp[i * 3 + 2] - bp[j * 3 + 2]) || i - j);
  const runFirst: number[] = [], runOf = new Int32Array(entries);
  for (let k = 0; k < entries; k++) {
    const i = byPos[k], j = k > 0 ? byPos[k - 1] : -1;
    if (j < 0 || bp[i * 3] !== bp[j * 3] || bp[i * 3 + 1] !== bp[j * 3 + 1] || bp[i * 3 + 2] !== bp[j * 3 + 2]) runFirst.push(i);   // (sorted by i within a run: its first is its least)
    runOf[i] = runFirst.length - 1;
  }
  const V = runFirst.length, runOrder = Int32Array.from(runFirst.keys()).sort((p, q) => runFirst[p] - runFirst[q]);
  const idOfRun = new Int32Array(V), first = new Int32Array(V);
  for (let v = 0; v < V; v++) { idOfRun[runOrder[v]] = v; first[v] = runFirst[runOrder[v]]; }
  const vertexOf = new Int32Array(entries);
  for (let i = 0; i < entries; i++) vertexOf[i] = idOfRun[runOf[i]];
  const ux = new Float64Array(V), uy = new Float64Array(V), uz = new Float64Array(V), h = new Float64Array(V);
  for (let v = 0; v < V; v++) {
    const i = first[v], x = bp[i * 3], y = bp[i * 3 + 1], z = bp[i * 3 + 2], l = Math.sqrt(x * x + y * y + z * z) || 1;
    ux[v] = x / l; uy[v] = y / l; uz[v] = z / l; h[v] = hm[i] / person;
  }
  // neighbours (each triangle's edges), ascending, packed: vertex v's are nb[nbAt[v] … nbAt[v + 1])
  const pairs: number[] = [];
  for (let t = 0; t + 2 < entries; t += 3) {
    const a = vertexOf[t], b = vertexOf[t + 1], c = vertexOf[t + 2];
    pairs.push(a * V + b, a * V + c, b * V + a, b * V + c, c * V + a, c * V + b);
  }
  const packed = Float64Array.from(pairs).sort();
  const nbAt = new Int32Array(V + 1), nbList: number[] = [];
  for (let k = 0; k < packed.length; k++) {
    if (k > 0 && packed[k] === packed[k - 1]) continue;
    const from = Math.floor(packed[k] / V), to = packed[k] - from * V;
    if (from === to) continue;
    nbList.push(to); nbAt[from + 1]++;
  }
  for (let v = 0; v < V; v++) nbAt[v + 1] += nbAt[v];
  const nb = Int32Array.from(nbList);
  const R = planet.radius / person;   // the planet's radius in person heights
  const angle = (a: number, b: number) => { const dx = ux[a] - ux[b], dy = uy[a] - uy[b], dz = uz[a] - uz[b]; return 2 * dasin(Math.min(1, Math.sqrt(dx * dx + dy * dy + dz * dz) / 2)); };
  // the height against the surroundings: the mean of the vertices within the breadth (a walk over the mesh), less its own.
  // (Within the breadth: the chord, squared, against the breadth's — the angle's test without an arcsine an edge.)
  const stamp = new Int32Array(V).fill(-1), queue = new Int32Array(V);
  const around = (v: number, people: number): number => {
    const half = dsin(Math.min(Math.PI, people / R) / 2), lim2 = 4 * half * half;
    const cx = ux[v], cy = uy[v], cz = uz[v];
    let head = 0, tail = 0, sum = 0, n = 0;
    queue[tail++] = v; stamp[v] = v * 2 + (people === RELIEF_WOODS.broadPeople ? 1 : 0);
    const mark = stamp[v];
    while (head < tail) {
      const u = queue[head++];
      sum += h[u]; n++;
      for (let k = nbAt[u], e = nbAt[u + 1]; k < e; k++) {
        const w = nb[k];
        if (stamp[w] === mark) continue;
        const dx = cx - ux[w], dy = cy - uy[w], dz = cz - uz[w];
        if (dx * dx + dy * dy + dz * dz <= lim2) { stamp[w] = mark; queue[tail++] = w; }
      }
    }
    return h[v] - sum / n;
  };
  const local = new Float64Array(V), broad = new Float64Array(V);
  for (let v = 0; v < V; v++) { local[v] = around(v, RELIEF_WOODS.localPeople); broad[v] = around(v, RELIEF_WOODS.broadPeople); }
  // the flow: from the highest vertex down, each sheds what it has gathered to its lowest neighbour below it
  const order = Array.from({ length: V }, (_, v) => v).sort((a, b) => h[b] - h[a] || a - b);
  const flow = new Float64Array(V).fill(1);
  for (const v of order) {
    let to = -1;
    for (let k = nbAt[v], e = nbAt[v + 1]; k < e; k++) { const w = nb[k]; if (h[w] < h[v] && (to < 0 || h[w] < h[to] || (h[w] === h[to] && w < to))) to = w; }
    if (to >= 0) flow[to] += flow[v];
  }
  // the slope and its fall's direction: the steepest descent to a neighbour (rise over run), and the lee — that fall
  // along the prevailing wind, the bands about the match's wind axis (core/windField.ts windAxis)
  const { x: ax, y: ay, z: az } = windAxis(planet);
  const slope = new Float64Array(V), lee = new Float64Array(V);
  for (let v = 0; v < V; v++) {
    let best = 0, to = -1;
    for (let k = nbAt[v], e = nbAt[v + 1]; k < e; k++) { const w = nb[k], run = angle(v, w) * R, s = run > 1e-9 ? (h[v] - h[w]) / run : 0; if (s > best) { best = s; to = w; } }
    slope[v] = best;
    if (to < 0) continue;
    // the fall's direction on the tangent plane, and the wind's: east about the axis, the band's way
    let fx = ux[to] - ux[v], fy = uy[to] - uy[v], fz = uz[to] - uz[v];
    const fd = fx * ux[v] + fy * uy[v] + fz * uz[v];
    fx -= fd * ux[v]; fy -= fd * uy[v]; fz -= fd * uz[v];
    const fl = Math.sqrt(fx * fx + fy * fy + fz * fz);
    let ex = ay * uz[v] - az * uy[v], ey = az * ux[v] - ax * uz[v], ez = ax * uy[v] - ay * ux[v];
    const el = Math.sqrt(ex * ex + ey * ey + ez * ez);
    if (fl < 1e-12 || el < 1e-12) continue;
    const lat = dasin(Math.max(-1, Math.min(1, ax * ux[v] + ay * uy[v] + az * uz[v]))), way = Math.sign(bandShare(lat)) || 1;
    ex *= way / el; ey *= way / el; ez *= way / el;
    lee[v] = (fx * ex + fy * ey + fz * ez) / fl;   // the ground falls the way the wind blows: the lee side
  }
  const score = new Float64Array(V);
  for (let v = 0; v < V; v++) score[v] = rawScore(local[v], broad[v], flow[v], slope[v], lee[v]);
  const sorted = Float64Array.from(score).sort();
  const at = (share: number) => sorted[Math.min(V - 1, Math.max(0, Math.floor(share * V)))];
  return { vertexOf, local, broad, flow, slope, lee, score, open: at(RELIEF_WOODS.openShare), dense: at(1 - RELIEF_WOODS.denseShare), top: sorted[V - 1] };
}

/** A stand's chance at `d` for `biome`: the relief's chance × the biome's ceiling. */
export function standChance(planet: PlanetState, relief: ReliefWoods, d: Vec3, biome: BiomeId): number {
  return reliefChance(planet, relief, d) * (RELIEF_WOODS.ceiling[biome] ?? 1);
}

/** A per-vertex field of the relief read at `d`: the triangle's three vertices' values, weighted by the point's barycentric place on their plane (NaN off the mesh). */
function reliefRead(planet: PlanetState, relief: ReliefWoods, d: Vec3, field: Float64Array): number {
  getSurfaceRadius(planet, d);
  const tri = lastSurfaceTriangle();
  if (tri < 0) return NaN;
  const bp = planet.basePositions, i0 = tri * 3;
  const p = [0, 1, 2].map(k => { const i = i0 + k, x = bp[i * 3], y = bp[i * 3 + 1], z = bp[i * 3 + 2], l = Math.sqrt(x * x + y * y + z * z) || 1; return [x / l, y / l, z / l]; });
  const w = barycentric(p[0], p[1], p[2], d);
  return w[0] * field[relief.vertexOf[i0]] + w[1] * field[relief.vertexOf[i0 + 1]] + w[2] * field[relief.vertexOf[i0 + 2]];
}

/** 393 (3) step 3: the windward exposure at `d`, 0 … 1 — the ground falling against the prevailing wind (the lee negated) on a slope that counts (full from a rise of a quarter): the exposed flank where a wood thins and its snags stand. */
export function reliefExposure(planet: PlanetState, relief: ReliefWoods, d: Vec3): number {
  const lee = reliefRead(planet, relief, d, relief.lee), slope = reliefRead(planet, relief, d, relief.slope);
  if (Number.isNaN(lee) || Number.isNaN(slope)) return 0;
  const against = Math.max(0, -lee);
  return against * against * Math.min(1, slope * 4);   // (squared: the flank that faces the wind squarely, not every slope with a windward lean)
}

/** The relief's chance of a stand at `d`, the biome's ceiling aside (393 (3) step 1: one factor of the woods density field, core/woodsDensity.ts): the ground's score read between the triangle's vertices, calibrated by the planet's own shares. */
export function reliefChance(planet: PlanetState, relief: ReliefWoods, d: Vec3): number {
  let s = reliefRead(planet, relief, d, relief.score);
  if (Number.isNaN(s)) s = (relief.open + relief.dense) / 2;
  // calibrated by the planet's own shares: open, a ramp, the dense core
  const W = RELIEF_WOODS;
  return s <= relief.open ? W.openChance
    : s >= relief.dense ? 0.9 + 0.1 * Math.min(1, (s - relief.dense) / Math.max(1e-9, relief.top - relief.dense))
    : W.openChance + (0.9 - W.openChance) * (s - relief.open) / Math.max(1e-9, relief.dense - relief.open);
}

/** The barycentric weights of direction `d` over the triangle (a, b, c) of unit directions (its plane; clamped to it). */
function barycentric(a: number[], b: number[], c: number[], d: Vec3): [number, number, number] {
  const v0 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]], v1 = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
  const dl = Math.sqrt(d.x * d.x + d.y * d.y + d.z * d.z) || 1, v2 = [d.x / dl - a[0], d.y / dl - a[1], d.z / dl - a[2]];
  const d00 = v0[0] * v0[0] + v0[1] * v0[1] + v0[2] * v0[2], d01 = v0[0] * v1[0] + v0[1] * v1[1] + v0[2] * v1[2];
  const d11 = v1[0] * v1[0] + v1[1] * v1[1] + v1[2] * v1[2], d20 = v2[0] * v0[0] + v2[1] * v0[1] + v2[2] * v0[2], d21 = v2[0] * v1[0] + v2[1] * v1[1] + v2[2] * v1[2];
  const den = d00 * d11 - d01 * d01;
  if (Math.abs(den) < 1e-18) return [1, 0, 0];
  let v = (d11 * d20 - d01 * d21) / den, w = (d00 * d21 - d01 * d20) / den;
  v = Math.max(0, Math.min(1, v)); w = Math.max(0, Math.min(1 - v, w));
  return [1 - v - w, v, w];
}
